import 'braid-design-system/reset';

import { ApolloProvider } from '@apollo/client';
import { MelwaysProvider } from '@seek/melways-react';
import { VocabProvider } from '@vocab/react';
import { Box, BraidProvider, makeLinkComponent } from 'braid-design-system';
import theme from 'braid-design-system/themes/seekJobs';
import {
  Outlet,
  Link as ReactRouterLink,
  useLoaderData,
} from 'react-router-dom';

import { getPageName } from 'src/core/utils/pageUtil';
import type { CategoryGroupsLoaderData } from 'src/mapper/groupMapper';
import { AnalyticsProvider } from 'src/provider/analytics';
import { AuthProvider } from 'src/provider/auth';
import ConfigProvider from 'src/provider/config';
import GroupProvider from 'src/provider/group';
import PageProvider from 'src/provider/page';
import { ProfileProvider } from 'src/provider/profile';
import type { RouteProps } from 'src/types';

const CustomLink = makeLinkComponent(({ href, ...restProps }, ref) => {
  const pageName = getPageName(href);
  if (pageName !== 'unknown') {
    return <ReactRouterLink ref={ref} to={href} {...restProps} />;
  }
  return <a ref={ref} href={href} {...restProps} />;
});

export const App = ({ client, config, clientOrServer }: RouteProps) => {
  const mode: 'client' | 'server' = clientOrServer ? clientOrServer : 'server';
  const data = useLoaderData() as {
    categoryGroups: CategoryGroupsLoaderData[];
  };

  return (
    <ConfigProvider config={{ ...config, clientOrServer: mode }}>
      <PageProvider>
        <ApolloProvider client={client}>
          <BraidProvider theme={theme} linkComponent={CustomLink}>
            <AuthProvider>
              <ProfileProvider>
                <GroupProvider value={data.categoryGroups}>
                  {mode === 'server' && (
                    <MelwaysProvider.Server
                      site={config.site}
                      staging={true}
                      url={config.absoluteUrl}
                    >
                      <VocabProvider
                        language={config.language}
                        locale={config.locale}
                      >
                        <AnalyticsProvider>
                          <Box role="main">
                            <Outlet context={{ client, config }} />
                          </Box>
                        </AnalyticsProvider>
                      </VocabProvider>
                    </MelwaysProvider.Server>
                  )}
                  {mode === 'client' && (
                    <MelwaysProvider.Client site={config.site} staging={true}>
                      <VocabProvider
                        language={config.language}
                        locale={config.locale}
                      >
                        <AnalyticsProvider>
                          <Box role="main">
                            <Outlet context={{ client, config }} />
                          </Box>
                        </AnalyticsProvider>
                      </VocabProvider>
                    </MelwaysProvider.Client>
                  )}
                </GroupProvider>
              </ProfileProvider>
            </AuthProvider>
          </BraidProvider>
        </ApolloProvider>
      </PageProvider>
    </ConfigProvider>
  );
};
